import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { gspx } from '@rsos/capstone';
import Checkbox from '@rsos/components/capstone/base/Checkbox';
import { isPublicSonarLayer } from '@rsos/components/capstone/common/Map/selectMarkerIcon';
import { isSpatialLayerDynamic } from '@rsos/components/capstone/common/Map/utils/spatialLayersHelpers';
import {
  STATIC_LAYERS_CARD,
  closeCard,
  setMapCardData,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import {
  toggleSpatialLayer,
  deactivateSpatialLayerStatus,
} from '../../actions/spatialLayers';
import useGISHelpers from '../../hooks/useGISHelpers';

/**
 * Component for handling the checkbox of a spatial layer
 * @param {Object} layer - The spatial layer
 * @param {Boolean} isDisabled - Is the layer disabled?
 * @param {Boolean} isGISSupplementalLayer - Is the layer a GIS supplemental layer?
 */
export default ({ layer, isDisabled, isGISSupplementalLayer }) => {
  const dispatch = useDispatch();
  const { activeGISServices, toggleGISService } = useGISHelpers();
  const isLayerDynamic = isSpatialLayerDynamic(layer);
  const layerID = layer.id;
  const layerKey = layer.key;
  const layerData = useSelector(state => state.mapCard?.mapCardData?.layerData);
  const { irpVersion } = useSelector(state => state.sinatra.irp);

  const handleCheckboxOnChange = () => {
    // layer.isChecked is in previous state here before reset
    // if is on, turn off layer
    // if is off, turn on layer
    const onOffText = layer.isChecked ? 'off' : 'on';
    const { name, source } = layer;

    if (!isLayerDynamic && source === layerData?.source) {
      dispatch(closeCard(STATIC_LAYERS_CARD));
      dispatch(setMapCardData(null));
    } else {
      //TODO: add handling for dynamic layers
    }
    gspx.trackCustomEvent('Data Map', {
      irpVersion,
      name: `Turn ${onOffText} ${name}`,
      Category: 'Spatial Layer',
      'Launched date': new Date(),
    });

    dispatch(toggleSpatialLayer(layerKey, isLayerDynamic, !layer.isChecked));
  };

  const handleGISCheckboxOnChange = event => {
    const onOffText = event.target.checked ? 'on' : 'off';
    const layerDisplayName = layer.display_name;

    gspx.trackCustomEvent('Data Map', {
      irpVersion,
      name: `Turn ${onOffText} ${layerDisplayName}`,
      Category: 'GIS Service',
      'Launched date': new Date(),
    });

    dispatch(toggleGISService(layer));
  };

  //TODO move this to each individual spatial object.
  const checkExpiredTime = () => {
    // set in GET_LATEST_ACTIVE_SPATIAL_LAYERS
    // for updating colors in active/inactive layers.
    const nowTS = Math.round(new Date().getTime());
    if (nowTS > layer.expiredTime) {
      dispatch(deactivateSpatialLayerStatus(layerID));
    }
  };

  useEffect(() => {
    const interval = setTimeout(checkExpiredTime, 1000);
    return () => {
      clearTimeout(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isGISSupplementalLayer ? (
    <Checkbox
      key={layerID}
      label={layer.display_name}
      isChecked={
        !!activeGISServices.filter(service => service.id === layerID).length
      }
      disabled={isDisabled}
      onChange={event => handleGISCheckboxOnChange(event)}
    />
  ) : (
    <Checkbox
      key={layerID}
      label={layer.title || layer.category?.name || ''}
      isChecked={layer.isChecked}
      description={isPublicSonarLayer(layer.key) ? layer.source : layer.name}
      onChange={handleCheckboxOnChange}
    />
  );
};
